import {
  Assessment,
  FilterKeys,
  GET_ASSESSMENTS_AVERAGE_QUERY,
  GetAssessmentsQuerySort,
  GetMarkbookSort,
  GetMarkbookSortBy,
  getSortParams,
  GroupType,
  GroupWithMembers,
  MarkbookArrangeBy,
  removeObjectEmptyArrayValues,
  removeObjectUndefinedNullValues,
  SORT_DIRECTION,
  useGetMarkbookInfiniteQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useSelectIdsContext } from '@schooly/utils/bulk-actions';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAssessmentsGradesForSchool } from '../../../hooks/useAssessmentsGradesForSchool';
import { useAssessmentsAverage } from '../Markbook/common/useAssessmentsAverage';
import { MarkbookViewSettings } from '../Markbook/MarkbookSettings/MarkbookSettings';

type UseAssessmentsMarkbookProps = {
  assessments: Assessment[];
  group: GroupWithMembers;
  settings: MarkbookViewSettings;
};

export const MARKBOOK_PAGE_SIZE = 99;

export const DEFAULT_ASSESSMENTS_SORT: GetAssessmentsQuerySort[] = [
  { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
];

const DEFAULT_MARKBOOK_SORT: GetMarkbookSort[] = [
  { sortBy: GetMarkbookSortBy.LastName, direction: SORT_DIRECTION.ASC },
  { sortBy: GetMarkbookSortBy.GivenName, direction: SORT_DIRECTION.ASC },
];

export const useAssessmentsMarkbook = ({
  group,
  settings,
  assessments,
}: UseAssessmentsMarkbookProps) => {
  const { schoolId = '' } = useAuth();
  const { validity } = group;
  const queryClient = useQueryClient();

  const dateFrom = validity?.date_from ?? validity?.from_school_year?.start;
  const dateTo = validity?.date_to ?? validity?.to_school_year?.end;
  const { selectedIds } = useSelectIdsContext();

  const {
    data: markbookData,
    params: markbookParams,
    error: markbookError,
    isLoading: isMarkbookLoading,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
  } = useGetMarkbookInfiniteQuery(
    {
      schoolId,
      pageSize: MARKBOOK_PAGE_SIZE,
      query: '',
      arrangeBy: MarkbookArrangeBy.Group,
      filters: removeObjectEmptyArrayValues({
        [FilterKeys.Group]: [group.id],
        [FilterKeys.Date]: dateFrom && dateTo ? [dateFrom, dateTo] : [],
        [FilterKeys.Subject]: group.subject ? [group.subject.id] : undefined,
        [FilterKeys.GroupTypes]: !group.subject ? [GroupType.TutorGroup] : undefined,
        [FilterKeys.Assessment]:
          selectedIds !== 'all' && selectedIds.size > 0
            ? Array.from(selectedIds)
            : assessments.map((a) => a.id),
      }),
      sort: DEFAULT_MARKBOOK_SORT,
    },
    {
      refetchOnMount: 'always',
    },
  );

  const { grades: selectLists, fetching: fetchingLists } = useAssessmentsGradesForSchool();

  const {
    averageData,
    subjectAverageData,
    isAverageLoading,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    benchmarkEnabled,
  } = useAssessmentsAverage({
    canFetchAverage: Boolean(
      schoolId && (settings.avgResults || settings.coloredCells) && markbookParams.filters,
    ),
    requestParams: {
      query: removeObjectUndefinedNullValues({
        assessment_ids: markbookParams.filters?.[FilterKeys.Assessment]?.length
          ? markbookParams.filters?.[FilterKeys.Assessment]?.join(',')
          : undefined,
        group_ids: markbookParams.filters?.[FilterKeys.Group]?.length
          ? markbookParams.filters?.[FilterKeys.Group]?.join(',')
          : undefined,
        date_from: markbookParams.filters?.[FilterKeys.Date]?.[0],
        date_to:
          markbookParams.filters?.[FilterKeys.Date]?.[1] ||
          markbookParams.filters?.[FilterKeys.Date]?.[0],
        sort_by: getSortParams([
          { columnTextId: 'assessment_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_evaluation_type', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_area_of_learning_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
        ]),
      }),

      params: { schoolId },
    },
    settings,
  });

  const handleSuccessfulEntryUpdate = useCallback(() => {
    queryClient.invalidateQueries([GET_ASSESSMENTS_AVERAGE_QUERY]);
  }, [queryClient]);

  return {
    selectedIds,
    dateFrom,
    dateTo,
    error: markbookError,
    isLoading: isMarkbookLoading,
    assessments,
    markbookParams,
    markbookData,
    averageData,
    isAverageLoading,
    subjectAverageData,
    selectLists,
    benchmarkEnabled,
    fetchNextPage,
    refetch,
    fetchingLists,
    hasNextPage,
    isFetchingNextPage,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    handleSuccessfulEntryUpdate,
  };
};
